import { useEffect, useState } from "react";
import { fetchSyncStatus, type PmsSyncStatus, type SyncStatusTonight } from "../services/syncStatusApi";
import { onSyncConfigChanged } from "../services/syncStatusEvents";

/** How often the indicator asks again while nothing is happening. */
export const SYNC_STATUS_POLL_MS = 60_000;
/** How often it asks while a run is in flight, so "syncing" ends when the run does. */
export const SYNC_STATUS_ACTIVE_POLL_MS = 15_000;
/**
 * How soon it asks again, once, after a first status that came without
 * tonight's reading. The server waits a quarter of a second for a reading it
 * does not hold and composes it behind the answer, so a few seconds on it is
 * there.
 */
export const SYNC_STATUS_TONIGHT_RETRY_MS = 3_000;
/** For how many statuses in a row a reading the server left out is carried over from the one before. */
export const SYNC_STATUS_TONIGHT_CARRIED = 2;

interface SyncStatusState {
    /** The refresh key the status was loaded for. */
    key: unknown;
    status: PmsSyncStatus | null;
}

/**
 * The active connection's sync status, kept fresh.
 *
 * Polls at rest once a minute and faster while a run is in flight; asks
 * again the moment the tab is looked at, since an operator coming back after
 * an hour should not read a minute-old verdict; asks again when the sync
 * settings are saved, because a switch that takes a minute to show reads as
 * one that did not register; and starts from nothing when `refreshKey`
 * changes, because the old property's verdict must not stand in for the new
 * one's for even a frame. A failed poll keeps the last verdict on screen —
 * the next one will say.
 *
 * The status also carries tonight's reading (`tonight`), which the rail draws
 * a line for, and the server leaves it out whenever it holds none: before it
 * has composed one, after a restart, behind a deploy. A line that comes and
 * goes moves every destination under it, so two things keep it still. A
 * reading the previous status carried is carried over a status without one,
 * for the same property and for two statuses at most; whether prices leave on
 * their own is still the newer status's to say (`push.autoSendsPrices`). And
 * a first status without a reading is asked for again a few seconds on, once,
 * instead of opening the visit without the line and moving the rail a minute
 * later. A server that never sends the reading costs that one extra request
 * and is then polled as before.
 */
export function useSyncStatus(refreshKey: unknown): PmsSyncStatus | null {
    const [state, setState] = useState<SyncStatusState>({ key: refreshKey, status: null });

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const schedule = (delay: number) => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => void load(), delay);
        };

        // Tonight's reading as the last status that stated it did, and how
        // many statuses since have left it out. This effect's own, so neither
        // outlives the property.
        let lastTonight: { connectionId: number; reading: SyncStatusTonight } | null = null;
        let leftOut = 0;
        let askedAgainForTonight = false;

        /** The status with the reading the one before carried, where it states none itself. */
        const withCarriedTonight = (status: PmsSyncStatus): PmsSyncStatus => {
            if (status.mode !== "on") {
                lastTonight = null;
                return status;
            }
            const stated: unknown = status.tonight;
            if (stated !== null && typeof stated === "object") {
                lastTonight = { connectionId: status.connectionId, reading: stated as SyncStatusTonight };
                leftOut = 0;
                return status;
            }
            if (!lastTonight || lastTonight.connectionId !== status.connectionId || leftOut >= SYNC_STATUS_TONIGHT_CARRIED) {
                lastTonight = null;
                return status;
            }
            leftOut += 1;
            const sendsPrices: unknown = status.push?.autoSendsPrices;
            return {
                ...status,
                tonight: typeof sendsPrices === "boolean"
                    ? { ...lastTonight.reading, autoSendsPrices: sendsPrices }
                    : lastTonight.reading,
            };
        };

        const load = async () => {
            let next: PmsSyncStatus | null = null;
            try {
                next = withCarriedTonight(await fetchSyncStatus());
                if (cancelled) return;
                setState({ key: refreshKey, status: next });
            } catch {
                if (cancelled) return;
            }
            const active = next?.mode === "on" && next.isSyncing;
            const usual = active ? SYNC_STATUS_ACTIVE_POLL_MS : SYNC_STATUS_POLL_MS;
            const lacksTonight = next?.mode === "on" && next.tonight == null;
            if (lacksTonight && !askedAgainForTonight) {
                askedAgainForTonight = true;
                schedule(Math.min(SYNC_STATUS_TONIGHT_RETRY_MS, usual));
                return;
            }
            schedule(usual);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") void load();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        // A setting the operator just changed must not wait out the poll.
        const unsubscribe = onSyncConfigChanged(() => void load());
        void load();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            unsubscribe();
        };
    }, [refreshKey]);

    return state.key === refreshKey ? state.status : null;
}
